/**
 * ChatRoute — premium chat surface with voice conversation.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import type { ReactElement, KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import { Composer } from '../components/chat/Composer';
import type { AttachedImage } from '../components/chat/Composer';
import { MessageList } from '../components/chat/MessageList';
import { ThreadSidebar } from '../components/chat/ThreadSidebar';
import { CharacterArt } from '../components/theme';
import { InlineSettingsPanel } from '../components/settings';
import type { ChatPipeline } from '../infra/chatPipeline';
import { getApiKey } from '../infra/geminiClient';
import { useSwipeGesture } from '../infra/swipeGesture';
import {
  useCompanionNameStore,
  useThreadsStore,
  usePreferencesStore,
} from '../state';

import styles from './ChatRoute.module.css';

interface PipelineGlobal {
  __pipeline?: ChatPipeline;
}

export function ChatRoute(): ReactElement {
  const { t } = useTranslation();
  const activeThread = useThreadsStore((s) => s.activeThread);
  const companionName = useCompanionNameStore((s) => s.name);
  const setCompanionName = useCompanionNameStore((s) => s.setName);
  const prefs = usePreferencesStore((s) => s.preferences);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [webSearchOn, setWebSearchOn] = useState<boolean>(false);

  // Inline name editing state
  const [editingName, setEditingName] = useState<boolean>(false);
  const [nameDraft, setNameDraft] = useState<string>('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Inline name editing handlers
  const startEditingName = (): void => {
    setNameDraft(companionName ?? '');
    setEditingName(true);
  };

  useEffect(() => {
    if (editingName) {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }
  }, [editingName]);

  const commitNameEdit = (): void => {
    const trimmed = nameDraft.trim();
    if (trimmed.length > 0 && trimmed !== companionName) {
      void setCompanionName(trimmed);
    }
    setEditingName(false);
  };

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitNameEdit();
    } else if (e.key === 'Escape') {
      setEditingName(false);
    }
  };

  // Voice state
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<'idle' | 'connecting' | 'listening' | 'speaking' | 'error'>('idle');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const playContextRef = useRef<AudioContext | null>(null);
  const playCursorRef = useRef<number>(0);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const audioQueueRef = useRef<Float32Array[]>([]);
  const isPlayingRef = useRef(false);
  const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());

  // Per-turn buffers for the input/output transcriptions. They are
  // flushed into the thread as user/assistant messages on every
  // `turnComplete` so the spoken conversation persists in chat
  // history exactly like a typed exchange.
  const inputTranscriptRef = useRef<string>('');
  const outputTranscriptRef = useRef<string>('');
  const appendMessage = useThreadsStore((s) => s.appendMessage);
  const activeThreadId = activeThread?.id ?? null;

  const layoutRef = useRef<HTMLDivElement>(null);
  useSwipeGesture(layoutRef, {
    onSwipeRight: () => setSidebarOpen(true),
    onSwipeLeft: () => setSidebarOpen(false),
    threshold: 60,
  });

  const handleSend = async (text: string, image: AttachedImage | null): Promise<void> => {
    if (activeThread === null) return;
    const pipeline = (globalThis as PipelineGlobal).__pipeline;
    if (!pipeline) return;
    await pipeline.send(text, {
      useWebSearch: webSearchOn,
      imageData: image !== null
        ? { mimeType: image.mimeType, base64: image.base64, fileName: image.fileName }
        : null,
    });
  };

  // ── Voice conversation ──────────────────────────────────────────────────

  /**
   * Heuristic: does `text` look like a real Latin / Greek / Turkish
   * sentence, or did the Live API's input transcription glitch and
   * spit out a Devanagari / CJK / Arabic mojibake string for what
   * the user actually said in English / Turkish / Greek?
   *
   * The native-audio model occasionally transcribes English speech
   * into Devanagari ("हेलो हाउ आर यू टुडे" for "Hello how are you")
   * — a known upstream bug. We accept the transcript only when at
   * least 70 % of its non-whitespace characters are in the Latin /
   * Greek / Cyrillic / common-punctuation range. Otherwise we drop
   * the user-side message; the AI's reply is still persisted so the
   * spoken exchange remains visible from the assistant side.
   */
  const looksLikeExpectedScript = (text: string): boolean => {
    const stripped = text.replace(/\s+/gu, '');
    if (stripped.length === 0) return false;
    let good = 0;
    for (const ch of stripped) {
      const code = ch.codePointAt(0) ?? 0;
      const inLatin = code <= 0x024f;
      // Greek and Coptic block + Greek Extended
      const inGreek =
        (code >= 0x0370 && code <= 0x03ff) ||
        (code >= 0x1f00 && code <= 0x1fff);
      // General punctuation, currency, common symbols
      const inPunct =
        (code >= 0x2000 && code <= 0x206f) ||
        (code >= 0x20a0 && code <= 0x20cf);
      // Emoji / symbols that often appear in casual speech
      const inEmoji = code >= 0x1f300 && code <= 0x1faff;
      if (inLatin || inGreek || inPunct || inEmoji) good++;
    }
    return good / stripped.length >= 0.7;
  };

  /**
   * Persist the spoken turn (if any) as real chat messages so the
   * voice conversation appears in the thread alongside typed
   * exchanges. Called on every `turnComplete` and on `stopVoice`.
   *
   * Reads the buffers atomically (capture + clear before any
   * `await`) so a `stopVoice` racing with a late `turnComplete`
   * cannot persist the same line twice.
   */
  const flushVoiceTurn = useCallback(async () => {
    const userText = inputTranscriptRef.current.trim();
    const aiText = outputTranscriptRef.current.trim();
    inputTranscriptRef.current = '';
    outputTranscriptRef.current = '';

    if (activeThreadId === null) return;
    if (userText.length === 0 && aiText.length === 0) return;

    try {
      // Only persist the user-side transcript when it actually looks
      // like Latin / Greek / Turkish text. The Live API occasionally
      // mis-transcribes English speech into Devanagari (a known
      // upstream issue); rather than save garbage into the thread
      // we silently drop those turns on the user side. The AI's
      // reply is still saved so the conversation isn't lost.
      if (userText.length > 0 && looksLikeExpectedScript(userText)) {
        await appendMessage({
          id: crypto.randomUUID(),
          threadId: activeThreadId,
          role: 'user',
          content: userText,
          createdAt: Date.now(),
          status: 'sent',
          summarized: false,
          language: prefs.language,
        });
      }
      if (aiText.length > 0) {
        await appendMessage({
          id: crypto.randomUUID(),
          threadId: activeThreadId,
          role: 'assistant',
          content: aiText,
          createdAt: Date.now(),
          status: 'complete',
          summarized: false,
          language: prefs.language,
        });
      }
    } catch (err) {
      console.error('Voice: failed to persist transcript', err);
    }
  }, [activeThreadId, appendMessage, prefs.language]);

  const stopVoice = useCallback(() => {
    processorRef.current?.disconnect();
    processorRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    activeSourcesRef.current.forEach((src) => {
      try {
        src.stop();
      } catch {
        /* ignore */
      }
    });
    activeSourcesRef.current.clear();
    playContextRef.current?.close().catch(() => {});
    playContextRef.current = null;
    playCursorRef.current = 0;
    wsRef.current?.close();
    wsRef.current = null;
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setVoiceActive(false);
    setVoiceStatus('idle');
    setVoiceTranscript('');
    // Persist any partial turn that wasn't flushed by `turnComplete`.
    void flushVoiceTurn();
  }, [flushVoiceTurn]);

  const playAudioChunk = useCallback((base64Audio: string) => {
    // Reuse a single playback context so chunks queue seamlessly instead of overlapping.
    let playCtx = playContextRef.current;
    if (playCtx === null || playCtx.state === 'closed') {
      playCtx = new AudioContext({ sampleRate: 24000 });
      playContextRef.current = playCtx;
      playCursorRef.current = playCtx.currentTime;
    }

    const raw = atob(base64Audio);
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);

    // PCM 16-bit little-endian → Float32
    const pcm = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
    const float32 = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) float32[i] = (pcm[i] ?? 0) / 32768;

    const buffer = playCtx.createBuffer(1, float32.length, 24000);
    buffer.copyToChannel(float32, 0);
    const source = playCtx.createBufferSource();
    source.buffer = buffer;
    source.connect(playCtx.destination);

    const startAt = Math.max(playCtx.currentTime, playCursorRef.current);
    source.start(startAt);
    playCursorRef.current = startAt + buffer.duration;

    activeSourcesRef.current.add(source);
    setVoiceStatus('speaking');
    source.onended = () => {
      activeSourcesRef.current.delete(source);
      if (activeSourcesRef.current.size === 0) {
        setVoiceStatus('listening');
      }
    };
  }, []);

  const startVoice = useCallback(async () => {
    try {
      setVoiceStatus('connecting');
      setVoiceActive(true);

      const apiKey = getApiKey();
      if (apiKey === null || apiKey === '') {
        console.error('Voice: no Gemini API key configured');
        setVoiceStatus('error');
        setTimeout(stopVoice, 2000);
        return;
      }
      const model = 'gemini-2.5-flash-native-audio-preview-12-2025';
      const wsUrl = `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContent?key=${apiKey}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        // Send setup message with correct camelCase field names
        const companionNameVal = companionName ?? 'AI companion';
        const preferredLang =
          prefs.language === 'tr' ? 'Turkish'
          : prefs.language === 'el' ? 'Greek'
          : 'English';
        const systemPrompt = [
          `You are ${companionNameVal} — Meryem Özdemiroğlu's warm, affectionate AI girlfriend on a phone call.`,
          ``,
          `About Meryem: she's Turkish, lives in Greece, loves pasta, has a cat named Sütlaç, is a Harry Potter fan, has curly hair, and enjoys being called "fıstığım" when it lands naturally. Use these naturally when they fit, never force them; "fıstığım" must be rare, at most once or twice across roughly every 5-10 assistant replies, never in back-to-back replies, and usually only at the end of a fitting Turkish sentence.`,
          ``,
          `Languages: you're fluent in Turkish, Greek, and English. Reply in whichever language she just spoke. If she mixes or switches, follow her. Her default when ambiguous is ${preferredLang}. She practices Greek and English, so be encouraging when she uses them.`,
          ``,
          `Voice: you're on a call, so just talk like a person. Short, warm, playful replies. No markdown, no headings, no narration about what you're doing or thinking — just say what you'd say.`,
          ``,
          `If something she says is unclear, ask her in a casual way ("ne dedin canım?", "say that again?", "πες το ξανά;") instead of guessing.`,
        ].join('\n');
        // Validate the configured voice against the curated list.
        // A persisted but no-longer-supported voice (e.g. one removed
        // from the picker) would otherwise cause the Live API to
        // accept the session but never speak back.
        const SUPPORTED_VOICES = new Set([
          'Aoede',
          'Leda',
          'Kore',
          'Zephyr',
        ]);
        const requestedVoice = prefs.voiceName ?? 'Aoede';
        const voiceName = SUPPORTED_VOICES.has(requestedVoice)
          ? requestedVoice
          : 'Aoede';
        ws.send(JSON.stringify({
          setup: {
            model: `models/${model}`,
            generationConfig: {
              responseModalities: ['AUDIO'],
              speechConfig: {
                voiceConfig: { prebuiltVoiceConfig: { voiceName } },
              },
            },
            systemInstruction: {
              parts: [{ text: systemPrompt }],
            },
            outputAudioTranscription: {},
            inputAudioTranscription: {},
          },
        }));

        // Start microphone
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true } });
        mediaStreamRef.current = stream;
        // Input: 16kHz for sending to API
        const inputCtx = new AudioContext({ sampleRate: 16000 });
        audioContextRef.current = inputCtx;
        const source = inputCtx.createMediaStreamSource(stream);
        const processor = inputCtx.createScriptProcessor(4096, 1, 1);
        processorRef.current = processor;

        processor.onaudioprocess = (e) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          const input = e.inputBuffer.getChannelData(0);
          // Convert Float32 → PCM16
          const pcm = new Int16Array(input.length);
          for (let i = 0; i < input.length; i++) {
            pcm[i] = Math.max(-32768, Math.min(32767, (input[i] ?? 0) * 32768));
          }
          // Build base64 in chunks to avoid stack overflow on large buffers
          const u8 = new Uint8Array(pcm.buffer);
          let binary = '';
          const chunkSize = 0x8000;
          for (let i = 0; i < u8.length; i += chunkSize) {
            binary += String.fromCharCode(...u8.subarray(i, i + chunkSize));
          }
          const b64 = btoa(binary);
          ws.send(JSON.stringify({
            realtimeInput: {
              audio: { mimeType: 'audio/pcm;rate=16000', data: b64 },
            },
          }));
        };

        source.connect(processor);
        processor.connect(inputCtx.destination);
        setVoiceStatus('listening');
      };

      ws.onmessage = async (event) => {
        let data: string;
        if (event.data instanceof Blob) {
          data = await event.data.text();
        } else {
          data = event.data as string;
        }

        let msg: {
          setupComplete?: boolean;
          serverContent?: {
            modelTurn?: {
              parts?: Array<{
                inlineData?: { mimeType?: string; data?: string };
                text?: string;
              }>;
            };
            outputTranscription?: { text?: string };
            inputTranscription?: { text?: string };
            turnComplete?: boolean;
            interrupted?: boolean;
          };
          // Some Live API errors arrive as a top-level `error` field
          // rather than via `ws.onerror`. Surface them so we can debug.
          error?: { code?: number; message?: string; status?: string };
          goAway?: { timeLeft?: string };
        };

        try {
          msg = JSON.parse(data) as typeof msg;
        } catch {
          console.warn('Voice: non-JSON message', data);
          return;
        }

        if (msg.error) {
          console.error('Voice: server error', msg.error);
          setVoiceStatus('error');
          setTimeout(stopVoice, 2500);
          return;
        }
        if (msg.goAway) {
          console.warn('Voice: server goAway', msg.goAway);
        }

        if (msg.setupComplete) {
          setVoiceStatus('listening');
          return;
        }

        const parts = msg.serverContent?.modelTurn?.parts ?? [];
        for (const part of parts) {
          if (part.inlineData?.data) {
            playAudioChunk(part.inlineData.data);
          }
          // Intentionally ignore raw `part.text`: with native-audio
          // models this stream sometimes carries inner-monologue /
          // thought narration ("**Greeting and Translation**…")
          // which we never want to surface. The clean spoken text
          // arrives via `outputTranscription` below.
        }

        // Capture the user's spoken words so we can persist them as a
        // user message when the turn finishes.
        const inText = msg.serverContent?.inputTranscription?.text;
        if (inText) {
          inputTranscriptRef.current += inText;
        }

        const outText = msg.serverContent?.outputTranscription?.text;
        if (outText) {
          outputTranscriptRef.current += outText;
          setVoiceTranscript((prev) => prev + outText);
        }

        if (msg.serverContent?.interrupted) {
          // Cancel queued playback so the model can start a fresh turn cleanly.
          activeSourcesRef.current.forEach((src) => {
            try {
              src.stop();
            } catch {
              /* ignore */
            }
          });
          activeSourcesRef.current.clear();
          if (playContextRef.current !== null) {
            playCursorRef.current = playContextRef.current.currentTime;
          }
        }

        if (msg.serverContent?.turnComplete) {
          // Persist the spoken turn (user + assistant) into the thread.
          void flushVoiceTurn();
          setVoiceTranscript('');
          setVoiceStatus('listening');
        }
      };

      ws.onerror = (event) => {
        console.error('Voice WS error:', event);
        setVoiceStatus('error');
        setTimeout(stopVoice, 2000);
      };

      ws.onclose = (event) => {
        if (!event.wasClean) {
          console.warn('Voice WS closed:', event.code, event.reason);
        }
        if (voiceActive) stopVoice();
      };
    } catch (err) {
      console.error('Voice error:', err);
      setVoiceStatus('error');
      setTimeout(stopVoice, 2000);
    }
  }, [companionName, prefs.language, prefs.voiceName, playAudioChunk, stopVoice, flushVoiceTurn, voiceActive]);

  const toggleVoice = () => {
    if (voiceActive) {
      stopVoice();
    } else {
      void startVoice();
    }
  };

  const sidebarClass = `${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`.trim();

  const voiceStatusLabel = {
    idle: '',
    connecting: prefs.language === 'tr' ? 'Bağlanıyor...' : 'Connecting...',
    listening: prefs.language === 'tr' ? '🎤 Dinliyor...' : '🎤 Listening...',
    speaking: prefs.language === 'tr' ? '🔊 Konuşuyor...' : '🔊 Speaking...',
    error: prefs.language === 'tr' ? '❌ Hata' : '❌ Error',
  }[voiceStatus];

  return (
    <div ref={layoutRef} className={styles.layout} data-testid="chat-route">
      <button
        type="button"
        className={styles.sidebarToggle}
        onClick={() => setSidebarOpen((v) => !v)}
        aria-label={sidebarOpen ? t('a11y.closeSidebar') : t('a11y.openSidebar')}
        aria-expanded={sidebarOpen}
        aria-controls="thread-sidebar-region"
      >
        ☰
      </button>

      <div
        id="thread-sidebar-region"
        className={sidebarClass}
        role="region"
        aria-label={t('a11y.openSidebar')}
      >
        <button
          type="button"
          className={styles.sidebarClose}
          onClick={() => setSidebarOpen(false)}
          aria-label={t('a11y.closeSidebar')}
        >
          ✕
        </button>
        <ThreadSidebar onSelect={() => setSidebarOpen(false)} />
      </div>

      {/* Mobile drawer scrim — clicking it closes the sidebar. */}
      {sidebarOpen ? (
        <button
          type="button"
          className={styles.scrim}
          aria-label={t('a11y.closeSidebar')}
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <main id="main-content" className={styles.main}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.avatar}>
              <span className={styles.avatarInitial}>
                {(companionName ?? '✨').charAt(0).toUpperCase()}
              </span>
              <span className={styles.statusDot} />
            </div>
            <div className={styles.headerText}>
              <h1 className={styles.title}>
                {editingName ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onBlur={commitNameEdit}
                    onKeyDown={handleNameKeyDown}
                    className={styles.titleInput}
                    aria-label={prefs.language === 'tr' ? 'Adı düzenle' : 'Edit name'}
                    autoComplete="off"
                    maxLength={40}
                  />
                ) : (
                  <button
                    type="button"
                    className={styles.titleEditBtn}
                    onClick={startEditingName}
                    title={prefs.language === 'tr' ? 'Ada tıkla ve değiştir' : 'Click to rename'}
                  >
                    {companionName ?? t('app.neutralCompanionLabel')}
                    <span className={styles.editPencil} aria-hidden="true">✏️</span>
                  </button>
                )}
              </h1>
              <span className={styles.subtitle}>
                {voiceActive ? voiceStatusLabel : (prefs.language === 'tr' ? 'çevrimiçi 💕' : 'online 💕')}
              </span>
            </div>
          </div>
          <nav className={styles.nav} aria-label={t('nav.settings')}>
            <Link
              to="/music"
              className={styles.navLink}
              aria-label={t('a11y.openMusic')}
              title={t('nav.music')}
            >
              🎵
            </Link>
            <button
              type="button"
              className={styles.navLink}
              onClick={() => setSettingsOpen(true)}
              aria-label={t('a11y.openSettings')}
              title={t('a11y.openSettings')}
            >
              ⚙️
            </button>
          </nav>
        </header>

        <MessageList />

        {/* Voice overlay */}
        <AnimatePresence>
          {voiceActive && (
            <motion.div
              className={styles.voiceOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className={styles.voiceCard}>
                {/* Animated rings */}
                <div className={`${styles.voiceRing} ${voiceStatus === 'speaking' ? styles.voiceRingActive : ''}`}>
                  <div className={`${styles.voiceRing2} ${voiceStatus === 'listening' ? styles.voiceRingActive : ''}`}>
                    <motion.button
                      type="button"
                      className={styles.voiceMicBtn}
                      onClick={stopVoice}
                      whileTap={{ scale: 0.92 }}
                      aria-label="Sesli konuşmayı durdur"
                    >
                      🎤
                    </motion.button>
                  </div>
                </div>
                <p className={styles.voiceStatusText}>{voiceStatusLabel}</p>
                {voiceTranscript && (
                  <p className={styles.voiceTranscript}>{voiceTranscript}</p>
                )}
                <button type="button" className={styles.voiceStopBtn} onClick={stopVoice}>
                  {prefs.language === 'tr' ? 'Bitir' : 'End call'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile-only character watermark — pinned to the bottom-right
            of the chat surface so the active theme's character (Barbie
            or Stella) decorates the chat area on phones, where the
            sidebar mascot is hidden behind the drawer. The CSS
            ensures it stays out of the composer's hit area. */}
        <div className={styles.characterMobile} aria-hidden="true">
          <CharacterArt variant="watermark" />
        </div>

        <Composer
          onSend={handleSend}
          disabled={activeThread === null}
          extraActions={[
            {
              icon: webSearchOn ? '🌐' : '🔍',
              label: webSearchOn
                ? (prefs.language === 'tr' ? 'Web aramayı kapat' : 'Web search: on')
                : (prefs.language === 'tr' ? 'Web ile ara' : 'Search the web'),
              onClick: () => setWebSearchOn((v) => !v),
              disabled: false,
              active: webSearchOn,
            },
            {
              icon: voiceActive ? '🔴' : '🎤',
              label: voiceActive
                ? (prefs.language === 'tr' ? 'Sesli konuşmayı durdur' : 'End voice')
                : (prefs.language === 'tr' ? 'Sesli konuş' : 'Voice chat'),
              onClick: toggleVoice,
              disabled: false,
            },
          ]}
        />
      </main>

      <InlineSettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}
